import { DevToolsPanelFooter } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsPanel/DevToolsPanelFooter';
import { DevToolsPanelHeader } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsPanel/DevToolsPanelHeader';
import { DevToolsTabBar } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsPanel/DevToolsTabBar';
import { TAB_ITEMS } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsPanel/index.constants';
import type { DevToolsTabValue } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsPanel/index.types';
import { CallsTab } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab';
import { DebugTab } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab';
import { DevicesTab } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DevicesTab';
import { EventsTab } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/EventsTab';
import { SipTab } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/SipTab';
import { WebRTCTab } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/WebRTCTab';
import {
  useClock,
  useDebugHistory,
  useEventBuffer,
  useWebRTCHealth,
} from 'Modules/softphone/components/SoftphoneDevTools/hooks';
import { useDevToolsStore } from 'Modules/softphone/components/SoftphoneDevTools/store';
import { getSipHealthStatus } from 'Modules/softphone/components/SoftphoneDevTools/utils';
import { useSoftphoneStore } from 'Modules/softphone/store';
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useShallow } from 'zustand/react/shallow';
import { Tabs, TabsContent } from '~/lib/ui/tabs';
import { cn } from '~/lib/utils';

const TAB_COMPONENTS: Record<DevToolsTabValue, React.FC> = {
  calls: CallsTab,
  events: EventsTab,
  sip: SipTab,
  webrtc: WebRTCTab,
  devices: DevicesTab,
  debug: DebugTab,
};

/**
 * DevToolsPanel - Left side panel with tabbed sections
 *
 * Contains all the DevTools tabs for SIP, Calls, WebRTC, Devices, and Debug
 * info. Opens as a side panel on the left, allowing the softphone to remain
 * visible on the right.
 */
export const DevToolsPanel: React.FC = () => {
  const { isOpen, close } = useDevToolsStore(
    useShallow((state) => ({ isOpen: state.isOpen, close: state.close }))
  );
  const [activeTab, setActiveTab] = useState<DevToolsTabValue>('calls');
  const [isAnimating, setIsAnimating] = useState(false);

  // Get softphone state for health indicators
  const { sipStatus, config, lastRegistrationTime } = useSoftphoneStore(
    useShallow((state) => ({
      sipStatus: state.status,
      config: state.config,
      lastRegistrationTime: state.lastRegistrationTime,
    }))
  );
  const webrtcHealth = useWebRTCHealth(isOpen);
  const eventBuffer = useEventBuffer(isOpen);
  const { debugHistory } = useDebugHistory();
  const now = useClock(isOpen);

  const handleClose = useCallback(() => {
    close();
  }, [close]);

  // Handle animation states
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  if (!isOpen && !isAnimating) {
    return null;
  }

  const panel = (
    <div
      className={cn(
        'softphone-devtools',
        'fixed bottom-0 left-0 top-0 z-[9998]',
        'w-[570px] max-w-[100vw]',
        // The panel sets its own type: 13px IBM Plex Sans is the size every
        // other value in here is sized against.
        'font-sp-sans text-[13px] text-zinc-200',
        'border-r border-zinc-800 bg-zinc-950 shadow-2xl',
        'flex flex-col',
        'transition-transform duration-300 ease-out',
        isOpen ? 'translate-x-0' : '-translate-x-full'
      )}
      onTransitionEnd={() => {
        if (!isOpen) setIsAnimating(false);
      }}
    >
      {/* Tabs Component - wraps everything */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as DevToolsTabValue)}
        className="flex flex-1 flex-col gap-0 overflow-hidden"
      >
        <DevToolsPanelHeader sipStatus={sipStatus} onClose={handleClose} />

        <DevToolsTabBar
          statusByTab={{
            sip: getSipHealthStatus(sipStatus),
            webrtc: webrtcHealth,
          }}
          countByTab={{
            events: eventBuffer.count,
            debug: debugHistory.length,
          }}
        />

        {/* Tabs Content */}
        <div className="relative flex-1 overflow-hidden">
          {TAB_ITEMS.map(({ value, contentClassName }) => {
            const TabContent = TAB_COMPONENTS[value];

            return (
              <TabsContent
                key={value}
                value={value}
                forceMount
                className={cn(
                  contentClassName,
                  activeTab !== value && 'hidden'
                )}
              >
                <TabContent />
              </TabsContent>
            );
          })}
        </div>

        <DevToolsPanelFooter
          identity={config?.uri ?? null}
          registeredAt={lastRegistrationTime}
          now={now}
        />
      </Tabs>
    </div>
  );

  return createPortal(panel, document.body);
};
